import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Measure from 'react-measure'
import colors from '../colors'
import * as d3 from 'd3'
import { debounce } from 'lodash'
import { useMemo } from 'react'
import { useTheme, makeStyles, alpha } from '@material-ui/core/styles'
import TooltipContent from './TooltipContent'
import { Popover } from '@material-ui/core'
import LoadingBubbles from 'components/LoadingBubbles'
import useMediaQuery from '@material-ui/core/useMediaQuery'

const useStyles = makeStyles((theme) => ({
  container: {
    lineHeight: 0,
    height: '100%',
    width: '100%',
    position: 'relative',
  },
  grid: {
    stroke: theme.palette.grey[200],
    strokeDasharray: '4 4',
  },
  loading: {
    display: 'block',
    position: 'absolute',
    top: '33%',
    left: '33%',
    width: '25%',
    height: '25%',
    margin: 'auto',
  },
  xLabel: {
    fill: alpha(theme.palette.text.primary, 0.7),
    textAnchor: 'middle',
  },
  yLabel: {
    alignmentBaseline: 'central',
    fill: alpha(theme.palette.text.primary, 0.7),
    textAnchor: 'end',
  },
}))

const segments = ['following', 'applying', 'applied']
const niceTicks = [1, 5, 10, 25, 50, 100, 250, 500, 1000]

const getTicks = (maxY) => {
  const tickCount = maxY < 5 ? maxY : 5
  let tickStep = Math.ceil(maxY / tickCount)

  //  If the step is more than 1,000 and is not evenly divisible by 1,000,
  //  then round the step up to the NEXT thousands
  //  ie:
  //      * step 1001 -> 2000
  //      * step 1999 -> 2000
  //      * step 5555 -> 6000
  if (tickStep > 1000) {
    if (tickStep % 1000 !== 0) {
      tickStep = Math.trunc(tickStep / 1000) * 1000 + 1000
    }
  } else {
    //  Otherwise, move up to the next 'nice' step value
    tickStep = niceTicks.find((value) => value >= tickStep)
  }

  //  Create ticks until we've passed the max value
  const ticks = [0]
  while (ticks[ticks.length - 1] < maxY) {
    ticks.push(tickStep * ticks.length)
  }

  return ticks
}

const formatTick = (tick) => {
  if (tick < 5000) return tick.toLocaleString()

  return Math.trunc(tick / 1000) + 'k'
}

const Graph = ({ data, loading }) => {
  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)
  const styles = useStyles()
  const [popoverData, setPopoverData] = useState(null)
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const debounceResize = useMemo(
    () =>
      debounce((newWidth, newHeight) => {
        setWidth(Math.floor(newWidth))
        setHeight(Math.floor(newHeight))
      }),
    [setWidth, setHeight],
  )

  const svgPadding = isSmall ? 0 : 20 //  The padding between the edge of the SVG and the SVG's content
  const barsPadding = isSmall ? 12 : 22 //  The padding between the first/last bar and the edge of the graph area (ie: for the grid lines)
  const strokeWidth = 8 //  The thickness of the bars
  const circleRadius = strokeWidth / 2 //  The radius of a circle (shown if the bar segment is too small)
  const yAxisWidth = isSmall ? 32 : 78 //  The width of the y axis label area, labels are centered in this area
  const xAxisHeight = 30 //  The height of the x axis label area
  const spacing = 6 //  The spacing between bar segments / circles
  const xScale = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([0, 12]) //  13 months (last 12 months + current month)
        .range([svgPadding + yAxisWidth + barsPadding, width - svgPadding - barsPadding]),
    [barsPadding, svgPadding, width, yAxisWidth],
  )
  const maxY = useMemo(
    () =>
      (data && data.months.reduce((prev, cur) => Math.max(prev, cur.following + cur.applying + cur.applied), 0)) || 50,
    [data],
  )
  const ticks = getTicks(maxY)

  const yScale = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([0, ticks[ticks.length - 1]])
        .range([svgPadding + xAxisHeight, height - svgPadding]),
    [ticks, svgPadding, height],
  )

  const series = useMemo(() => {
    if (!data || loading || !height) {
      return []
    }

    const stack = d3.stack().keys(segments)
    const series = stack(data.months)

    const isTooSmall = (segment) => segment[0] !== segment[1] && segment[1] - segment[0] < circleRadius

    for (let i = 0; i < series[0].length; i++) {
      const applied = series[0][i]
      const applying = series[1][i]
      const following = series[2][i]

      //  rendered if any if the length of the segment is > 0
      applied.isRendered = applied[0] !== applied[1]
      applying.isRendered = applying[0] !== applying[1]
      following.isRendered = following[0] !== following[1]

      //  scale the positions to the graph's coordinate space
      applied[0] = yScale(applied[0])
      applied[1] = yScale(applied[1])
      applying[0] = yScale(applying[0])
      applying[1] = yScale(applying[1])
      following[0] = yScale(following[0])
      following[1] = yScale(following[1])

      //  Shrink the segments by spacing to apply a gap
      if (applied.isRendered) {
        applied[0] += spacing
        applied[1] -= spacing
      }
      if (applying.isRendered) {
        applying[0] += spacing
        applying[1] -= spacing
      }
      if (following.isRendered) {
        following[0] += spacing
        following[1] -= spacing
      }

      if (isTooSmall(applied)) {
        applied.renderCircle = true

        if (applying.isRendered) {
          applying[0] += 2 * circleRadius - (applied[1] - applied[0] + spacing)
          applying[1] += 2 * circleRadius - (applied[1] - applied[0] + spacing)
        }
        if (following.isRendered) {
          following[0] += 2 * circleRadius - (applied[1] - applied[0] + spacing)
          following[1] += 2 * circleRadius - (applied[1] - applied[0] + spacing)
        }
      }

      if (isTooSmall(applying)) {
        applying.renderCircle = true

        if (following.isRendered) {
          following[0] += 2 * circleRadius - (applying[1] - applying[0] + spacing)
          following[1] += 2 * circleRadius - (applying[1] - applying[0] + spacing)
        }
      }

      if (isTooSmall(following)) {
        following.renderCircle = true
      }

      //  Translate the segments to be bottom aligned against the x axis
      applied[0] = height - applied[0]
      applied[1] = height - applied[1]
      applying[0] = height - applying[0]
      applying[1] = height - applying[1]
      following[0] = height - following[0]
      following[1] = height - following[1]
    }

    return series
  }, [circleRadius, data, height, loading, yScale])
  return (
    <React.Fragment>
      {popoverData && (
        <Popover
          open={Boolean(popoverData.element)}
          anchorEl={popoverData.element}
          onClose={() => setPopoverData(null)}
          anchorOrigin={{
            horizontal: 'center',
            vertical: 'top',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          style={{
            pointerEvents: 'none',
          }}
          disableRestoreFocus
        >
          <TooltipContent segment={popoverData.data} />
        </Popover>
      )}
      <Measure
        onResize={(contentRect) => {
          if (contentRect && contentRect.entry) {
            debounceResize(contentRect.entry.width, contentRect.entry.height)
          }
        }}
      >
        {({ measureRef }) => {
          return (
            <div
              ref={measureRef}
              className={styles.container}
            >
              {loading && <LoadingBubbles className={styles.loading} />}
              <svg
                height="100%"
                width="100%"
                viewBox={`0 0 ${width} ${height}`}
              >
                {ticks.map((tick) => (
                  <React.Fragment key={tick}>
                    <text
                      className={styles.yLabel}
                      x={svgPadding + yAxisWidth / 2}
                      y={height - yScale(tick)}
                    >
                      {formatTick(tick)}
                    </text>
                    <line
                      className={styles.grid}
                      x1={xScale(0) - barsPadding}
                      x2={xScale(12) + barsPadding}
                      y1={height - yScale(tick)}
                      y2={height - yScale(tick)}
                      strokeWidth="4"
                    />
                  </React.Fragment>
                ))}
                {series.map((segment, i) => (
                  <React.Fragment key={segments[i]}>
                    {segment.map((dataPoint, j) =>
                      !dataPoint.isRendered ? null : (
                        <g
                          key={j}
                          transform={`translate(${xScale(j)}, ${0})`}
                          onMouseOver={(event) => {
                            setPopoverData({
                              data: dataPoint,
                              element: event.target,
                            })
                          }}
                          onMouseLeave={() => {
                            setPopoverData(null)
                          }}
                        >
                          {dataPoint.renderCircle ? (
                            <circle
                              data-value={JSON.stringify(dataPoint.data)}
                              data-segment={segments[i]}
                              cx={0}
                              cy={dataPoint[0]}
                              r={circleRadius}
                              fill={colors[segments[i]]}
                            />
                          ) : (
                            <line
                              data-value={JSON.stringify(dataPoint.data)}
                              data-segment={segments[i]}
                              x1={0}
                              x2={0}
                              y1={dataPoint[0]}
                              y2={dataPoint[1]}
                              stroke={colors[segments[i]]}
                              strokeLinecap="round"
                              strokeWidth={strokeWidth}
                            />
                          )}
                          <text
                            className={styles.xLabel}
                            y={height - svgPadding / 2}
                          >
                            {dataPoint.data.month}
                          </text>
                        </g>
                      ),
                    )}
                  </React.Fragment>
                ))}
              </svg>
            </div>
          )
        }}
      </Measure>
    </React.Fragment>
  )
}

const monthPropType = PropTypes.shape({
  applied: PropTypes.number.isRequired,
  applying: PropTypes.number.isRequired,
  following: PropTypes.number.isRequired,
  month: PropTypes.string.isRequired,
}).isRequired

Graph.propTypes = {
  data: PropTypes.shape({
    gradYear: PropTypes.string.isRequired,
    months: PropTypes.arrayOf(monthPropType).isRequired,
    totals: PropTypes.shape({
      applied: PropTypes.number.isRequired,
      applying: PropTypes.number.isRequired,
      following: PropTypes.number.isRequired,
      month: PropTypes.string.isRequired,
    }),
  }),
  loading: PropTypes.bool.isRequired,
}

export default Graph
